<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import BlogSidebar from '@/components/publicSite/BlogSidebar.vue'
import PublicSiteFooter from '@/components/publicSite/PublicSiteFooter.vue'
import PublicSiteHeader from '@/components/publicSite/PublicSiteHeader.vue'
import { $api } from '@/utils/api'

definePage({
  meta: {
    layout: 'blank',
    public: true,
  },
})

interface BlogPostDetail {
  id: number
  title: string
  slug: string
  excerpt: string | null
  body: string
  cover_image_url: string | null
  cover_image_alt: string | null
  author_name: string | null
  published_at: string | null
}

const route = useRoute()

const post = ref<BlogPostDetail | null>(null)
const isLoading = ref(true)
const notFound = ref(false)

const formatDate = (date: string | null) => {
  if (!date)
    return ''

  return new Date(date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })
}

const shareUrl = computed(() => typeof window === 'undefined' ? '' : window.location.href)

const shareLinks = computed(() => {
  if (!post.value)
    return { whatsapp: '', facebook: '', x: '', linkedin: '' }

  const url = encodeURIComponent(shareUrl.value)
  const text = encodeURIComponent(post.value.title)

  return {
    whatsapp: `https://wa.me/?text=${text}%20${url}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}`,
    x: `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
  }
})

const linkCopied = ref(false)

const copyLink = async () => {
  try {
    await navigator.clipboard.writeText(shareUrl.value)
    linkCopied.value = true
    setTimeout(() => { linkCopied.value = false }, 2000)
  } catch {
    // Clipboard API non disponibile: nessun errore bloccante, l'utente può
    // comunque copiare l'URL manualmente dalla barra degli indirizzi.
  }
}

const setupSEO = () => {
  if (typeof document === 'undefined' || !post.value)
    return

  document.title = `${post.value.title} - Blog Appforge`
}

const fetchPost = async () => {
  isLoading.value = true
  notFound.value = false
  try {
    post.value = await $api<BlogPostDetail>(`/blog/${route.params.slug}`)
    setupSEO()
  } catch {
    notFound.value = true
  } finally {
    isLoading.value = false
  }
}

onMounted(fetchPost)

watch(() => route.params.slug, fetchPost)
</script>

<template>
  <div class="post-premium">
    <PublicSiteHeader />

    <div v-if="isLoading" class="d-flex justify-center py-16">
      <VProgressCircular indeterminate color="primary" size="48" />
    </div>

    <div v-else-if="notFound || !post" class="d-flex align-center justify-center" style="min-height:60vh;">
      <div class="text-center">
        <p class="text-h5 mb-6" style="color:#F1F5F9;">Articolo non trovato.</p>
        <VBtn color="primary" to="/blog">Torna al blog</VBtn>
      </div>
    </div>

    <article v-else>
      <header class="post-hero" :style="post.cover_image_url ? `background-image:linear-gradient(rgba(15,23,42,0.55),rgba(15,23,42,0.9)),url('${post.cover_image_url}')` : ''">
        <div class="container text-center">
          <span v-if="post.published_at" class="post-date-badge mb-6">{{ formatDate(post.published_at) }}</span>
          <h1 class="text-h1 hero-title mb-4">{{ post.title }}</h1>
          <p v-if="post.author_name" class="post-author">di {{ post.author_name }}</p>
        </div>
      </header>

      <section class="post-body-section py-16">
        <div class="container">
          <VRow>
            <VCol cols="12" md="8">
              <div class="post-body" v-html="post.body" />

              <div class="share-bar">
                <span class="share-label">Condividi:</span>
                <a :href="shareLinks.whatsapp" target="_blank" rel="noopener" class="share-btn" aria-label="WhatsApp">
                  <VIcon icon="tabler-brand-whatsapp" size="20" />
                </a>
                <a :href="shareLinks.facebook" target="_blank" rel="noopener" class="share-btn" aria-label="Facebook">
                  <VIcon icon="tabler-brand-facebook" size="20" />
                </a>
                <a :href="shareLinks.x" target="_blank" rel="noopener" class="share-btn" aria-label="X">
                  <VIcon icon="tabler-brand-x" size="20" />
                </a>
                <a :href="shareLinks.linkedin" target="_blank" rel="noopener" class="share-btn" aria-label="LinkedIn">
                  <VIcon icon="tabler-brand-linkedin" size="20" />
                </a>
                <button type="button" class="share-btn" aria-label="Copia link" @click="copyLink">
                  <VIcon :icon="linkCopied ? 'tabler-check' : 'tabler-link'" size="20" />
                </button>
              </div>
            </VCol>

            <VCol cols="12" md="4">
              <BlogSidebar />
            </VCol>
          </VRow>
        </div>
      </section>
    </article>

    <PublicSiteFooter />
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;600;800&display=swap');

.post-premium {
  font-family: 'Outfit', sans-serif;
  background-color: #0F172A;
  color: #F1F5F9;
  min-height: 100vh;
}

.container { max-width: 1600px; margin: 0 auto; padding: 0 40px; }

.post-hero {
  padding: 100px 0 60px; background-size: cover; background-position: center;
}
.hero-title { color: #FFFFFF; max-width: 900px; margin-left: auto; margin-right: auto; }
.post-date-badge {
  display: inline-block; padding: 6px 16px; background: rgba(99, 102, 241, 0.15);
  border: 1px solid rgba(99, 102, 241, 0.25); border-radius: 100px; color: #A5B4FC;
  font-weight: 700; font-size: 13px; text-transform: uppercase; letter-spacing: 0.06em;
}
.post-author { color: #E5EEF9; font-size: 15px; }

.post-body {
  color: #E5EEF9; font-size: 17px; line-height: 1.8;
}
.post-body :deep(h2) { color: #FFFFFF; font-size: 26px; font-weight: 700; margin: 40px 0 16px; }
.post-body :deep(h3) { color: #FFFFFF; font-size: 21px; font-weight: 700; margin: 32px 0 12px; }
.post-body :deep(p) { margin: 0 0 20px; }
.post-body :deep(ul), .post-body :deep(ol) { margin: 0 0 20px; padding-left: 24px; }
.post-body :deep(li) { margin-bottom: 8px; }
.post-body :deep(a) { color: #A5B4FC; }
.post-body :deep(img) { max-width: 100%; border-radius: 12px; margin: 24px 0; }
.post-body :deep(blockquote) {
  border-left: 3px solid #6366F1; padding-left: 20px; margin: 24px 0; color: #C7D2FE; font-style: italic;
}

.share-bar {
  display: flex; align-items: center; gap: 12px; margin-top: 48px; padding-top: 32px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
.share-label { color: #8078A0; font-size: 14px; font-weight: 600; margin-right: 4px; }
.share-btn {
  width: 40px; height: 40px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
  background: rgba(255, 255, 255, 0.05); border: 1px solid rgba(255, 255, 255, 0.1); color: #F1F5F9;
  text-decoration: none; cursor: pointer; transition: all 0.2s ease;
}
.share-btn:hover { border-color: #6366F1; background: rgba(99, 102, 241, 0.15); }

@media (max-width: 600px) {
  .container { padding: 0 20px; }
}
</style>
